/**
 * Hello by Flow logo component.
 *
 * The source asset is a portrait PNG with a stylized soundwave mark, the
 * "Hello" wordmark, "by Flow" subwordmark, and the tagline
 * "Calls. Messages. Flowing Together." on a white background. Callers
 * control size via the `size` prop (height in pixels); the image keeps
 * its native aspect ratio.
 *
 * For tight surfaces where you only want the soundwave mark, pass
 * `compact` to crop to the icon area (uses object-position to shift the
 * source so the wordmark + tagline fall outside the visible box).
 *
 * Source: official Hello by Flow logo (extracted from the brand PDF, Jun 2026),
 * served from /public/brand with a transparent background.
 */

export const LOGO_SRC = "/brand/hbf-logo-3d.png";

export default function Logo({ size = 32, compact = false, className = "", alt = "Hello by Flow", inverted, ...rest }) {
  // `inverted` is accepted for API compatibility (dark-mode call sites) but
  // deliberately consumed here so it never leaks onto the DOM.
  void inverted;
  if (compact) {
    // Aspect-ratio-agnostic compact mark. Uses `object-fit: contain` so the
    // full logo always renders inside the square container regardless of the
    // source's orientation (portrait, landscape, or square). No crop math,
    // no manual offsets — swapping the LOGO_SRC asset in future will "just
    // work" without re-tuning this component.
    return (
      <div
        className={`inline-block ${className}`}
        style={{
          height: size,
          width: size,
          padding: 2,
          borderRadius: 8,
          background: "#fff",
          display: "grid",
          placeItems: "center",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
        data-testid="logo-mark"
        {...rest}
      >
        <img
          src={LOGO_SRC}
          alt={alt}
          draggable={false}
          style={{
            maxWidth: "100%",
            maxHeight: "100%",
            width: "auto",
            height: "auto",
            objectFit: "contain",
            display: "block",
          }}
        />
      </div>
    );
  }
  return (
    <img
      src={LOGO_SRC}
      alt={alt}
      draggable={false}
      className={className}
      style={{ height: size, width: "auto", display: "inline-block" }}
      data-testid="logo-full"
      {...rest}
    />
  );
}
